import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Header, API, Footer } from "../App";
import { useWidgetMode } from "@/lib/widgetMode";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Camera, Upload, X, Check, Image } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ContactModal from "../components/ContactModal";
import { brandConfig } from "@/lib/brandConfig";

const PHOTO_TYPES = [
  { id: "front", label: "Front View", description: "Full front of vehicle" },
  { id: "rear", label: "Rear View", description: "Full back of vehicle" },
  { id: "driver_side", label: "Driver Side", description: "Full left side" },
  { id: "passenger_side", label: "Passenger Side", description: "Full right side" },
  { id: "interior", label: "Interior", description: "Dashboard and front seats" },
  { id: "damage", label: "Damage (if any)", description: "Any visible damage" },
];

const PhotoUploadScreen = ({ state, updateState }) => {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState({});
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const fileInputRefs = useRef({});
  const { isCompact: compact } = useWidgetMode();

  useEffect(() => {
    if (!state.offer || !state.conditionAnswers.length) {
      navigate('/');
    }
  }, [state.offer, state.conditionAnswers, navigate]);

  const handleFileSelect = async (photoType, file) => {
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(prev => ({ ...prev, [photoType]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/offers/upload-photo?offer_id=${state.offer.offer_id}&photo_type=${photoType}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.success) {
        // Create preview URL
        const previewUrl = URL.createObjectURL(file);
        
        setPhotos(prev => ({
          ...prev,
          [photoType]: {
            id: response.data.photo_id,
            preview: previewUrl,
            filename: file.name
          }
        }));
        
        toast.success(`${PHOTO_TYPES.find(p => p.id === photoType)?.label} uploaded`);
      }
    } catch (err) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(prev => ({ ...prev, [photoType]: false }));
    }
  };

  const handleRemovePhoto = (photoType) => {
    if (photos[photoType]?.preview) {
      URL.revokeObjectURL(photos[photoType].preview);
    }
    setPhotos(prev => {
      const updated = { ...prev };
      delete updated[photoType];
      return updated;
    });
  };

  const handleSubmit = async (contactInfo = undefined) => {
    // Show contact modal if not already provided
    if (contactInfo === undefined) {
      setShowContactModal(true);
      return;
    }

    setLoading(true);
    setShowContactModal(false);

    try {
      // Lock the offer with contact info
      const response = await axios.post(`${API}/offers/lock`, {
        offer_id: state.offer.offer_id,
        condition_answers: state.conditionAnswers,
        photo_ids: Object.values(photos).map(p => p.id),
        contact: contactInfo,
        partner_key: brandConfig.partnerKey
      });

      updateState({
        photos: Object.values(photos).map(p => p.id),
        lockedOffer: response.data,
        contactInfo: contactInfo
      });

      navigate('/locked-offer');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to lock offer');
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = (contactInfo) => {
    handleSubmit(contactInfo);
  };

  if (!state.offer) return null;
  const fieldHeight = compact ? "h-11" : "h-14";

  return (
    <div className={compact ? "flex flex-col bg-white" : "min-h-screen flex flex-col bg-white"}>
      <Header showProgress currentStep={5} variant="light" />

      <main className={compact ? "py-4" : "min-h-[calc(100vh-80px)] py-12 sm:py-20"}>
        <div className="container-flow">
          {/* Header */}
          <div className={`text-center opacity-0 animate-fade-in-up ${compact ? "mb-4" : "mb-8"}`}>
            <span className={`text-label text-blue-600 block ${compact ? "mb-1" : "mb-4"}`}>STEP 6 OF 7</span>
            <h1 className={compact ? "text-xl font-medium tracking-tight text-slate-900 mb-1" : "text-heading-2 mb-2"}>
              Upload Vehicle Photos
            </h1>
            <p className={compact ? "text-sm text-slate-500" : "text-body"}>
              Photos help us verify your vehicle's condition and provide a final offer.
            </p>
          </div>

          {/* Photo Grid */}
          <div className={`photo-grid opacity-0 animate-fade-in-up animate-stagger-2 ${compact ? "mt-3" : "mt-6"}`}>
            {PHOTO_TYPES.map((photoType) => {
              const photo = photos[photoType.id];
              const isUploading = uploading[photoType.id];

              return (
                <div key={photoType.id} className="relative">
                  <input
                    type="file"
                    ref={el => fileInputRefs.current[photoType.id] = el}
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFileSelect(photoType.id, e.target.files?.[0])}
                    data-testid={`photo-input-${photoType.id}`}
                  />
                  
                  <div
                    onClick={() => !photo && fileInputRefs.current[photoType.id]?.click()}
                    className={`photo-slot ${photo ? 'has-photo' : ''}`}
                    data-testid={`photo-slot-${photoType.id}`}
                  >
                    {isUploading ? (
                      <div className="flex flex-col items-center">
                        <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
                        <span className="text-xs text-slate-500 mt-2">Uploading...</span>
                      </div>
                    ) : photo ? (
                      <img 
                        src={photo.preview} 
                        alt={photoType.label}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center text-center p-4">
                        <Camera className="w-8 h-8 text-slate-400 mb-2" />
                        <span className="text-sm font-medium text-slate-700">{photoType.label}</span>
                        <span className="text-xs text-slate-500">{photoType.description}</span>
                      </div>
                    )}
                  </div>

                  {/* Remove button */}
                  {photo && (
                    <button
                      onClick={() => handleRemovePhoto(photoType.id)}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
                      data-testid={`remove-photo-${photoType.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}

                  {/* Success indicator */}
                  {photo && (
                    <div className="absolute bottom-2 right-2 w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tips */}
          <div className={`p-4 bg-blue-50 rounded-xl opacity-0 animate-fade-in-up animate-stagger-3 ${compact ? "mt-3" : "mt-8"}`}>
            <h3 className="font-medium text-slate-900 mb-2">Photo Tips</h3>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>• Take photos in good lighting</li>
              <li>• Capture the entire vehicle in frame</li>
              <li>• Include any damage or imperfections</li>
              <li>• Make sure images are clear and in focus</li>
            </ul>
          </div>

          {/* Actions */}
          <div className={`flex flex-col @sm:flex-row opacity-0 animate-fade-in-up animate-stagger-4 ${compact ? "mt-4 gap-2" : "mt-8 gap-4"}`}>
            <Button
              variant="outline"
              onClick={() => navigate('/condition')}
              className={`flex-1 ${fieldHeight} btn-secondary`}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <Button
              onClick={() => handleSubmit()}
              disabled={loading}
              className={`flex-1 ${fieldHeight} btn-primary disabled:opacity-50`}
              data-testid="lock-offer-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Locking Offer...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Lock My Offer
                  <ArrowRight className="w-5 h-5" />
                </span>
              )}
            </Button>
          </div>
        </div>
      </main>
      {!compact && <Footer minimal />}

      {/* Contact Modal */}
      <ContactModal
        open={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSubmit={handleContactSubmit}
        loading={loading}
      />
    </div>
  );
};

export default PhotoUploadScreen;
